import { runInAction } from "mobx";
import { isNil } from "lodash";


import { useStore } from "@vromo/mobx-react/mobx-react";
import { useToasts } from "@vromo/ui/components/Toasts";
import { RootStore } from "../../stores/RootStore";
import { Platform } from "@vromo/models/Integration";
import { IntegrationApiLocationAction } from "@vromo/api/Integration";
import Bugsnag from "@vromo/analytics/bugsnag";
import { useAccount } from "../../account";
import Tracking from '@vromo/analytics';
import { useIntegrationsSession } from "./useIntegrationsSession";
import { useIntegrationsPage } from "./useIntegrationsPage";
import { GraphErrorType } from "@vromo/api";


/** Leverage Integrations Page MOBx state context and common functions
 * @note requires container component to be mobx "observer"
 */
export const useIntegrationsActions = () => {


    const showToast = useToasts();
    const { createIntegrationToken } = useAccount()
    const {
        setLoading,
        integrationsApi,
        updateFetchedIntegrations, fetchedIntegrations,
        fetched, setFetched
    } = useIntegrationsPage()
    const { retryWithNewToken, setSession, getSession } = useIntegrationsSession()
    const {
        authStore: { role: { group }, user: { contact }, status: { group: AccountGroup } }
    } = useStore<RootStore>('rootStore');
    const email = contact?.email ?? "nobody@email.com"

    /** fetch integrations 
     * @note right now BE will only return instance stored in tray
     */
    const fetchIntegrations = async () => {
        setLoading(true)
        try {
            const { result: currentIntegrations } = await retryWithNewToken(integrationsApi.list)
            updateFetchedIntegrations(currentIntegrations)
            Tracking.track(`Integration fetched by user '${email}' (group '${group!.id!}')`);
        }
        catch (err) {
            console.error(err)
            Bugsnag.notify(err)
            showToast("Could not fetch existing integrations, please retry", "error")
        }
        finally {
            setLoading(false)
        }
    }

    /** delete an existing integration. 
     * @note right now only works with a specific deletion url from the tray instance 
     */
    const deleteIntegration = async (platform: Platform) => {

        try {
            setLoading(true)
            runInAction(() => fetchedIntegrations.get(platform)!.status = "deleting")
            const { deleted, message } = await retryWithNewToken(() =>
                integrationsApi.deleteInstance(fetchedIntegrations.get(platform)?.features?.delete)()
            )
            if (!deleted) {
                throw new Error(message)
            }
            fetchedIntegrations.has(platform) && runInAction(() => fetchedIntegrations.delete(platform))
            showToast(`'${platform}' integration was deleted`)
            Tracking.track(`${platform} integration deleted by user '${email}' (group '${group!.id!}')`);
        }
        catch (err) {
            Bugsnag.notify(err)
            fetchedIntegrations.has(platform) && runInAction(() => fetchedIntegrations.get(platform)!.status = undefined)
            showToast(`'${platform}' integration could not deleted`, "error")
        }
        finally {
            setLoading(false)
        }
    }

    /** creates a new integration if it doesnt exist. Returns tray instance config wizard if available */
    const createIntegrationConfig = async (platform: Platform) => {

        try {
            setLoading(true)
            // todo: optionally refresh vromo access token ? 
            const vromo_access_token = !fetchedIntegrations.has(platform) ?
                await createIntegrationToken(group!.id!, `${platform}-integration-app`)! :
                undefined
            showToast(`${platform} integration configuration has started`)
            const { auth_only_url, instance_id, wizard_url } =
                await retryWithNewToken(() =>
                    integrationsApi.create({
                        platform,
                        vromo_access_token,
                        // provide group name for better understanding of user-group in tray listing
                        vromo_email: group!.name!
                    }))
            runInAction(() => fetchedIntegrations.set(platform, {
                authenticated: false,
                completed: false,
                enabled: false,
                ext_title: "",
                outdated: false,
                ...fetchedIntegrations.get(platform),
                ext_ref: instance_id,
                platform,
                features: {
                    ...fetchedIntegrations.get(platform)?.features,
                    configAuthOnlyUrl: auth_only_url,
                    wizardUrl: wizard_url
                }
            }))
            Tracking.track(`${platform} integration config started (created) by user '${email}' (group '${group!.id!}')`);
        }
        catch (err) {
            Bugsnag.notify(err)
            fetchedIntegrations.has(platform) &&
                runInAction(() => fetchedIntegrations.get(platform)!.status = undefined)
            showToast(`Could not init the configuration for a new ${platform} integration: ${(err as GraphErrorType).message}`, "error")
        }
        finally {
            setLoading(false)
        }
    }

    /** clears out tray config wizard url  */
    const cancelIntegrationConfig = (platform: Platform) => {
        if (
            fetchedIntegrations.has(platform) &&
            fetchedIntegrations.get(platform)!.features!.wizardUrl
        )
            runInAction(() => delete fetchedIntegrations.get(platform)!.features!.wizardUrl)
    }

    /** run a specif workflo defined by the integration solution instance, that finish the configuration of the integration profile */
    const finishIntegrationConfig = async (platform: Platform) => {

        try {
            setLoading(true)
            runInAction(() => fetchedIntegrations.get(platform)!.status = "synching")
            showToast(`${platform} Integration will be ready in less than a minute, please wait...`)
            const { completed, message } = await retryWithNewToken(() => integrationsApi.finishConfig(fetchedIntegrations.get(platform)!.features!.finish!)())
            if (!completed)
                throw new Error(`Configuration could not finish: ${message}`)
            Tracking.track(`${platform} integration config was finished by user '${email}' (group '${group!.id!}')`);
            fetchedIntegrations.has(platform) && runInAction(() => fetchedIntegrations.get(platform)!.status = "synching")
        }
        catch (err) {
            Bugsnag.notify(err)
            fetchedIntegrations.has(platform) && runInAction(() => fetchedIntegrations.get(platform)!.status = "error")
            showToast(`Could not finish the configuration for ${platform} integration: ${(err as GraphErrorType).message}`, "error")
        }
        finally {
            setLoading(false)
            await fetchIntegrations()
        }
    }

    /** fetch, list locations, or enable / disable a given integration location */
    const queryLocations = async (platform: Platform, action: IntegrationApiLocationAction, locationId?: string) => {
        try {
            setLoading(true)
            runInAction(() => fetchedIntegrations.get(platform)!.status = "synching")

            if (["enable", "disable"].includes(action)) {
                await retryWithNewToken(() =>
                    integrationsApi.toogleLocation(fetchedIntegrations.get(platform)!.features!.locations!)({
                        body: { action, id: locationId! }
                    })
                )
                Tracking.track(`group '${group!.id!}' ${platform} integration location '${locationId}' was ${action} by user '${email}'`);
                const updatedLocation = (fetchedIntegrations.get(platform)!.ext_locations ?? []).find((location) => location.ext_site_id === locationId)
                !isNil(updatedLocation) && runInAction(() => updatedLocation.enabled = !updatedLocation.enabled)
            }
            else {
                const { result } = await retryWithNewToken(() =>
                    integrationsApi.queryLocations(fetchedIntegrations.get(platform)!.features!.locations!)({ query: { action } })
                )
                Tracking.track(`${platform} integration locations were ${action} by user '${email}' (group '${group!.id!}')`);
                runInAction(() => fetchedIntegrations.get(platform)!.ext_locations = result)
            }
        }
        catch (err) {
            Bugsnag.notify(err)
            console.error(err)
            fetchedIntegrations.has(platform) && runInAction(() => fetchedIntegrations.get(platform)!.ext_locations = [])
            showToast(`Could not complete the '${action}' location(s) action for ${platform} integration`, "error")
        }
        finally {
            fetchedIntegrations.has(platform) && runInAction(() => fetchedIntegrations.get(platform)!.status = undefined)
            setLoading(false)
        }
    }


    /** restart the page context and refresh existing integations */
    const restartContext = async () => {
        if (group && AccountGroup) {
            runInAction(() => {
                integrationsApi.group = group!.id!
                integrationsApi.rootGroup = AccountGroup!
            })
            if (fetched) {
                setSession()
                updateFetchedIntegrations([])
                setFetched(false)
            }
            await getSession()
            await fetchIntegrations()
            setFetched(true)
        }
    }



    return {
        fetchIntegrations,
        createIntegrationConfig,
        cancelIntegrationConfig,
        deleteIntegration,
        finishIntegrationConfig,
        queryLocations,
        restartContext
    }

}